import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, Check, Loader2 } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const initialForm = { name: "", email: "", phone: "", subject: "General enquiry", message: "" };

// Extracted animation configs to avoid re-creating on every render
const fadeIn = { initial: { opacity: 0, y: 20 }, animate: { opacity: 1, y: 0 }, exit: { opacity: 0 }, transition: { duration: 0.4 } };
const fadeOnly = { initial: { opacity: 0 }, animate: { opacity: 1 }, exit: { opacity: 0 } };

function parseErrorDetail(detail) {
  if (Array.isArray(detail)) {
    return detail.map((d) => d.msg || JSON.stringify(d)).join(", ");
  }
  if (typeof detail === "string") {
    return detail;
  }
  return "Something went wrong. Please try again.";
}

export default function ContactForm() {
  const [form, setForm] = useState(initialForm);
  const [status, setStatus] = useState("idle"); // idle | sending | success | error
  const [error, setError] = useState("");

  const update = (field) => (event) => {
    setForm((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const submit = async (event) => {
    event.preventDefault();
    setStatus("sending");
    setError("");
    try {
      const response = await fetch(`${BACKEND}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(parseErrorDetail(data.detail));
      }
      setStatus("success");
      setForm(initialForm);
    } catch (err) {
      setStatus("error");
      setError(err.message || "Network error");
    }
  };

  return (
    <div className="contact-form-wrap" data-testid="contact-form-wrap">
      <AnimatePresence mode="wait">
        {status === "success" ? (
          <motion.div
            key="success"
            className="contact-success"
            data-testid="contact-success"
            {...fadeIn}
          >
            <div className="contact-success-mark">
              <Check size={26} />
            </div>
            <h3>Thank you.</h3>
            <p>
              Your enquiry has been received. A member of the Nawan team will
              respond within one business day.
            </p>
            <button
              className="contact-submit"
              onClick={() => setStatus("idle")}
              data-testid="contact-send-another"
              type="button"
            >
              Send another message <ArrowUpRight size={16} />
            </button>
          </motion.div>
        ) : (
          <motion.form
            key="form"
            onSubmit={submit}
            className="contact-form"
            data-testid="contact-form"
            {...fadeOnly}
          >
            <div className="contact-row two">
              <label>
                <span>Your name</span>
                <input
                  required
                  value={form.name}
                  onChange={update("name")}
                  placeholder="Full name"
                  data-testid="contact-name-input"
                />
              </label>
              <label>
                <span>Email</span>
                <input
                  required
                  type="email"
                  value={form.email}
                  onChange={update("email")}
                  placeholder="you@example.com"
                  data-testid="contact-email-input"
                />
              </label>
            </div>
            <div className="contact-row two">
              <label>
                <span>Phone (optional)</span>
                <input
                  value={form.phone}
                  onChange={update("phone")}
                  placeholder="+92 300 ..."
                  data-testid="contact-phone-input"
                />
              </label>
              <label>
                <span>Subject</span>
                <select
                  value={form.subject}
                  onChange={update("subject")}
                  data-testid="contact-subject-select"
                >
                  <option>General enquiry</option>
                  <option>Product information</option>
                  <option>Partnership / distribution</option>
                  <option>Careers</option>
                  <option>Media & press</option>
                </select>
              </label>
            </div>
            <label>
              <span>Message</span>
              <textarea
                required
                rows={5}
                value={form.message}
                onChange={update("message")}
                placeholder="Tell us how we can help."
                data-testid="contact-message-input"
              />
            </label>
            {status === "error" && (
              <p className="contact-error" data-testid="contact-error">{error}</p>
            )}
            <button
              type="submit"
              className="contact-submit"
              disabled={status === "sending"}
              data-testid="contact-submit-button"
            >
              {status === "sending" ? (
                <>
                  <Loader2 size={16} className="spin" /> Sending
                </>
              ) : (
                <>
                  Send enquiry <ArrowUpRight size={16} />
                </>
              )}
            </button>
          </motion.form>
        )}
      </AnimatePresence>
    </div>
  );
}
